import { gql, useQuery } from "urql";
import { useLocale } from "@acto/web-common/locales";
import { useCurrencyContext } from "@acto/taskpane/core/contexts/TenantSettingsContext";
import { formatCurrency, formatDayMonthYear } from "@acto/taskpane/field-sales-2/utils/format";
import { LineItemInvoiceStatus, type OrderInvoiceStatus } from "@acto/gql/graphql";

const OrderDetailsQuery = gql`
  query GetOrderDetails($orderId: ID!) {
    order(id: $orderId) {
      id
      date
      incomingOrder
      externalReference
      invoiceStatus
      lineItems {
        id
        productName
        productExternalReference
        incomingOrder
        quantity
        invoiceStatus
      }
    }
  }
`;

interface LineItemNode {
  id: string;
  productName: string;
  productExternalReference: string;
  incomingOrder: number | null;
  quantity: number;
  invoiceStatus: LineItemInvoiceStatus | null;
}

interface OrderNode {
  id: string;
  date: string;
  incomingOrder: number | null;
  externalReference: string;
  invoiceStatus: OrderInvoiceStatus | null;
  lineItems: LineItemNode[];
}

interface OrderDetailsData {
  order: OrderNode | null;
}

export interface LineItemDisplay {
  id: string;
  productName: string;
  productExternalReference: string;
  total: number | null;
  formattedTotal: string;
  invoiceStatus: LineItemInvoiceStatus | null;
}

export interface OrderDetails {
  id: string;
  externalReference: string;
  date: string;
  formattedDate: string;
  incomingOrder: number | null;
  formattedIncomingOrder: string;
  invoiceStatus: OrderInvoiceStatus | null;
  /** null when any line's invoice status, or an invoiced line's order intake, is unknown, because the invoiced sum
   *  would then be understated. */
  formattedInvoicedIncomingOrder: string | null;
  lineItems: LineItemDisplay[];
}

export function useOrderDetails(orderId: string | undefined): {
  isPending: boolean;
  error: Error | undefined;
  order: OrderDetails | null;
} {
  const { locale } = useLocale();
  const currency = useCurrencyContext();

  const [result] = useQuery<OrderDetailsData>({
    query: OrderDetailsQuery,
    variables: { orderId: orderId ?? "" },
    requestPolicy: "cache-first",
    pause: !orderId,
  });

  const { data, fetching, stale, error } = result;

  const lineItems = data?.order?.lineItems ?? [];
  const invoicedLines = lineItems.filter((li) => li.invoiceStatus === LineItemInvoiceStatus.Invoiced);
  const isInvoicedSumKnown =
    lineItems.every((li) => li.invoiceStatus != null) && invoicedLines.every((li) => li.incomingOrder != null);
  const invoicedIncomingOrder = invoicedLines.reduce((sum, li) => sum + (li.incomingOrder ?? 0), 0);

  const order =
    data?.order == null
      ? null
      : {
          id: data.order.id,
          externalReference: data.order.externalReference,
          date: data.order.date,
          formattedDate: formatDayMonthYear(data.order.date, locale),
          incomingOrder: data.order.incomingOrder ?? null,
          formattedIncomingOrder:
            data.order.incomingOrder != null ? formatCurrency(data.order.incomingOrder, currency, locale) : "—",
          invoiceStatus: data.order.invoiceStatus ?? null,
          formattedInvoicedIncomingOrder: isInvoicedSumKnown
            ? formatCurrency(invoicedIncomingOrder, currency, locale)
            : null,
          lineItems: (data.order.lineItems || []).map((li) => ({
            id: li.id,
            productName: li.productName,
            productExternalReference: li.productExternalReference,
            total: li.incomingOrder ?? null,
            formattedTotal: li.incomingOrder != null ? formatCurrency(li.incomingOrder, currency, locale) : "—",
            invoiceStatus: li.invoiceStatus ?? null,
          })),
        };

  return {
    isPending: fetching || stale,
    error,
    order,
  };
}
